import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  deleteObject,
  type FirebaseStorage,
  getDownloadURL,
  getMetadata,
  getStorage,
  listAll,
  ref,
  type StorageReference,
  type UploadMetadata,
  type UploadResult,
  uploadBytes,
} from 'firebase/storage';
import {
  decryptData,
  encryptData,
  exportKey,
  generateMasterKey,
  importMasterKey,
} from './encryptionService';
import { app, firestore } from './firebase';
import httpClient from './http/httpClient';

interface StorageUploadResult {
  success: boolean;
  path: string;
  downloadURL: string;
  storageType: StorageType;
  ref: StorageReference;
}

interface StorageDeleteResult {
  success: boolean;
  storageType: StorageType;
}

interface StorageDownloadURLResult {
  success: boolean;
  url: string;
  storageType: StorageType;
  isEncrypted?: boolean;
  encryptionScope?: 'user' | 'client';
}

interface StorageFileItem {
  name: string;
  fullPath: string;
  storageType: StorageType;
}

interface StorageListFilesResult {
  success: boolean;
  files: StorageFileItem[];
  storageType: StorageType;
}

type StorageType = 'firebase' | 'sharepoint';

class StorageService {
  private storageType: StorageType;
  private clientId: string | null;
  private initialized: boolean;
  private encryptionKey: CryptoKey | null; // User Key
  private clientKey: CryptoKey | null; // Client Key
  private customStorage: FirebaseStorage | null;
  private encryptionEnabled: boolean;

  constructor() {
    this.storageType = 'firebase';
    this.clientId = null;
    this.initialized = false;
    this.encryptionKey = null;
    this.clientKey = null;
    this.customStorage = null;
    this.encryptionEnabled = false;
  }

  // For testing
  setStorageInstance(storage: FirebaseStorage) {
    this.customStorage = storage;
  }

  async initialize(clientId: string): Promise<void> {
    this.clientId = clientId;
    this.initialized = true;
    // Attempt to load client key in background
    this.loadClientKey(clientId).catch((_err) => {
      // Ignore
    });
  }

  private async loadClientKey(clientId: string): Promise<void> {
    if (this.clientKey) {
      return;
    }

    try {
      const keyDocRef = doc(firestore, 'clients', clientId, 'keys', 'main');
      const keyDoc = await getDoc(keyDocRef);

      if (keyDoc.exists()) {
        const data = keyDoc.data();
        if (data?.key) {
          this.clientKey = await importMasterKey(data.key);
        }
      }
    } catch (_e) {
      // Ignore
    }
  }

  /**
   * Retrieves or creates the Client Encryption Key.
   * If creating, it saves it to Firestore.
   */
  async getClientEncryptionKey(): Promise<CryptoKey> {
    await this.ensureInitialized();
    if (!this.clientId) {
      throw new Error('Storage service not initialized with Client ID');
    }

    // 1. Check if already loaded
    if (this.clientKey) {
      return this.clientKey;
    }

    // 2. Fetch from Firestore
    const keyDocRef = doc(firestore, 'clients', this.clientId, 'keys', 'main');
    const keyDoc = await getDoc(keyDocRef);

    if (keyDoc.exists()) {
      const data = keyDoc.data();
      if (data?.key) {
        this.clientKey = await importMasterKey(data.key);
        return this.clientKey;
      }
    }

    // 3. Create new if not exists (and we are authorized - handled by Firestore Rules)
    try {
      const newKey = await generateMasterKey();
      const exportedKey = await exportKey(newKey);

      await setDoc(keyDocRef, {
        key: exportedKey,
        createdAt: new Date(),
        algorithm: 'AES-GCM',
      });

      this.clientKey = newKey;
      return newKey;
    } catch (error) {
      throw new Error(
        `Failed to generate/save client key. Ensure you have admin permissions. ${error}`
      );
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized && this.clientId) {
      await this.initialize(this.clientId);
    }
  }

  setClientId(clientId: string): void {
    this.clientId = clientId;
    this.initialized = false;
    this.clientKey = null;
  }

  setStorageType(type: StorageType): void {
    this.storageType = type;
  }

  setEncryptionKey(key: CryptoKey | null): void {
    this.encryptionKey = key;
  }

  getEncryptionKey(): CryptoKey | null {
    return this.encryptionKey;
  }

  setEncryptionEnabled(enabled: boolean): void {
    this.encryptionEnabled = enabled;
  }

  isEncryptionEnabled(): boolean {
    return this.encryptionEnabled;
  }

  getStorageType(): StorageType {
    return this.storageType;
  }

  async uploadFile(filePath: string, file: File): Promise<StorageUploadResult> {
    await this.ensureInitialized();

    if (this.storageType === 'sharepoint') {
      return await this.uploadToSharePoint(filePath, file);
    } else {
      return await this.uploadToFirebase(filePath, file);
    }
  }

  private async uploadToFirebase(filePath: string, file: File): Promise<StorageUploadResult> {
    const storage: FirebaseStorage = this.customStorage || getStorage(app);
    const storageRef: StorageReference = ref(storage, filePath);

    let dataToUpload: Blob | Uint8Array | ArrayBuffer = file;
    const metadata: UploadMetadata = { customMetadata: {}, contentType: file.type };

    const isSupportTicket = filePath.startsWith('support_tickets/');
    const isPublic = filePath.startsWith('public/');
    const isPartnerProfile = filePath.includes('/partner_profile/');
    const isBrandAsset = filePath.includes('/brand_assets/');
    const isKnowledgeBase = filePath.includes('/knowledge_base/');

    // Determine the correct encryption key to use
    let keyToUse = this.encryptionKey;
    if (isKnowledgeBase && this.clientKey) {
      keyToUse = this.clientKey;
    }

    if (
      this.encryptionEnabled &&
      keyToUse &&
      this.storageType === 'firebase' &&
      !isSupportTicket &&
      !isPublic &&
      !isPartnerProfile &&
      !isBrandAsset
    ) {
      const fileBuffer = await file.arrayBuffer();
      const encryptedBuffer = await encryptData(keyToUse, fileBuffer);
      dataToUpload = new Uint8Array(encryptedBuffer);
      metadata.customMetadata = { ...metadata.customMetadata, encrypted: 'true' };
    }

    const snapshot: UploadResult = await uploadBytes(storageRef, dataToUpload, metadata);
    const downloadURL: string = await getDownloadURL(snapshot.ref);

    return {
      success: true,
      path: filePath,
      downloadURL: downloadURL,
      storageType: 'firebase',
      ref: snapshot.ref,
    };
  }

  private async uploadToSharePoint(filePath: string, file: File): Promise<StorageUploadResult> {
    // 1. Get the upload session URL from the backend
    const response = await httpClient.post<{ success: boolean; uploadUrl: string }>(
      '/getSharepointUploadSession',
      { filePath }
    );

    if (!response?.uploadUrl) {
      throw new Error('Failed to generate SharePoint upload session.');
    }

    const uploadUrl = response.uploadUrl;
    const fileSize = file.size;
    const chunkSize = 327680 * 10; // 3.2 MB chunks
    let start = 0;

    // 2. Upload file in chunks directly to Microsoft Graph
    while (start < fileSize) {
      const end = Math.min(start + chunkSize, fileSize);
      const chunk = file.slice(start, end);

      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Length': `${chunk.size}`,
          'Content-Range': `bytes ${start}-${end - 1}/${fileSize}`,
        },
        body: chunk,
      });

      if (!uploadRes.ok) {
        const text = await uploadRes.text();
        throw new Error(
          `SharePoint chunk upload failed: ${uploadRes.status} ${uploadRes.statusText} - ${text}`
        );
      }

      start = end;
    }

    // Note: We return a dummy ref since SharePoint doesn't use Firebase StorageReferences
    return {
      success: true,
      path: filePath,
      downloadURL: '',
      storageType: 'sharepoint',
      ref: {} as StorageReference,
    };
  }

  async deleteFile(filePath: string): Promise<StorageDeleteResult> {
    await this.ensureInitialized();

    if (this.storageType === 'sharepoint') {
      return await this.deleteFromSharePoint(filePath);
    } else {
      return await this.deleteFromFirebase(filePath);
    }
  }

  private async deleteFromFirebase(filePath: string): Promise<StorageDeleteResult> {
    const storage: FirebaseStorage = this.customStorage || getStorage(app);
    const storageRef: StorageReference = ref(storage, filePath);

    await deleteObject(storageRef);

    return {
      success: true,
      storageType: 'firebase',
    };
  }

  private async deleteFromSharePoint(filePath: string): Promise<StorageDeleteResult> {
    return await this.deleteFromFirebase(filePath);
  }

  async getDownloadURL(filePath: string): Promise<StorageDownloadURLResult> {
    await this.ensureInitialized();

    if (this.storageType === 'sharepoint') {
      return await this.getSharePointDownloadURL(filePath);
    } else {
      return await this.getFirebaseDownloadURL(filePath);
    }
  }

  private async getFirebaseDownloadURL(filePath: string): Promise<StorageDownloadURLResult> {
    const storage: FirebaseStorage = this.customStorage || getStorage(app);
    const storageRef: StorageReference = ref(storage, filePath);

    const downloadURL: string = await getDownloadURL(storageRef);

    // Check if encrypted
    let isEncrypted = false;
    let encryptionScope: 'user' | 'client' = 'user';

    try {
      const metadata = await getMetadata(storageRef);
      if (metadata.customMetadata?.encrypted === 'true') {
        isEncrypted = true;
      }
      if (metadata.customMetadata?.encryptionScope === 'client') {
        encryptionScope = 'client';
      }
    } catch (_e) {
      // Ignore metadata fetch error
    }

    return {
      success: true,
      url: downloadURL,
      storageType: 'firebase',
      isEncrypted,
      encryptionScope,
    };
  }

  private async getSharePointDownloadURL(filePath: string): Promise<StorageDownloadURLResult> {
    return await this.getFirebaseDownloadURL(filePath);
  }

  async downloadAndDecryptFile(filePath: string): Promise<Blob> {
    await this.ensureInitialized();

    if (this.storageType === 'sharepoint') {
      throw new Error('Client-side decryption not supported for SharePoint');
    }

    const { url, isEncrypted, encryptionScope } = await this.getFirebaseDownloadURL(filePath);

    const response = await fetch(url);
    const blob = await response.blob();

    if (isEncrypted) {
      let keyToUse: CryptoKey | null = null;

      if (encryptionScope === 'client') {
        keyToUse = await this.getClientEncryptionKey();
      } else {
        keyToUse = this.encryptionKey;
      }

      if (!keyToUse) {
        throw new Error(
          `File is encrypted (${encryptionScope} scope) but no decryption key is available.`
        );
      }

      const buffer = await blob.arrayBuffer();
      const decryptedBuffer = await decryptData(keyToUse, buffer);
      return new Blob([decryptedBuffer], { type: blob.type });
    }

    return blob;
  }

  async listFiles(directoryPath: string): Promise<StorageListFilesResult> {
    await this.ensureInitialized();

    if (this.storageType === 'sharepoint') {
      return await this.listSharePointFiles(directoryPath);
    } else {
      return await this.listFirebaseFiles(directoryPath);
    }
  }

  private async listFirebaseFiles(directoryPath: string): Promise<StorageListFilesResult> {
    const storage: FirebaseStorage = this.customStorage || getStorage(app);
    const storageRef: StorageReference = ref(storage, directoryPath);

    const result = await listAll(storageRef);

    const files: StorageFileItem[] = result.items.map((item) => ({
      name: item.name,
      fullPath: item.fullPath,
      storageType: 'firebase' as StorageType,
    }));

    return {
      success: true,
      files: files,
      storageType: 'firebase',
    };
  }

  private async listSharePointFiles(directoryPath: string): Promise<StorageListFilesResult> {
    return await this.listFirebaseFiles(directoryPath);
  }

  createRef(filePath: string): StorageReference {
    if (this.storageType !== 'firebase') {
      throw new Error('createRef is only available for Firebase storage');
    }

    const storage: FirebaseStorage = this.customStorage || getStorage(app);
    return ref(storage, filePath);
  }
}

const storageService = new StorageService();

export default storageService;
export type {
  StorageDeleteResult,
  StorageDownloadURLResult,
  StorageFileItem,
  StorageListFilesResult,
  StorageType,
  StorageUploadResult,
};
export { StorageService };
